import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Cpu, RefreshCw, Loader2, Thermometer, HardDrive, Activity } from 'lucide-react';

export default function GpuHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHealth = async () => {
    setLoading(true);
    try {
      const res = await adminApi.gpuHealth();
      setHealth(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadHealth(); }, []);

  const gpu = health?.gpu?.gpu || health?.gpu || {};
  const services = health?.services?.services || {};

  return (
    <div data-testid="gpu-health" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">GPU Health</h2>
          <p className="text-muted-foreground mt-1">Monitor GPU server status and services</p>
        </div>
        <Button variant="outline" onClick={loadHealth} disabled={loading} data-testid="refresh-health">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">GPU Status</p>
                <p className="text-xl font-bold mt-1 capitalize">{gpu.status || health?.gpu?.status || 'Unknown'}</p>
                <p className="text-xs text-muted-foreground mt-1">{gpu.name || 'N/A'}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">VRAM Usage</p>
                <p className="text-xl font-bold mt-1">
                  {gpu.vram_used_gb ? `${gpu.vram_used_gb}GB` : 'N/A'} / {gpu.vram_total_gb ? `${gpu.vram_total_gb}GB` : 'N/A'}
                </p>
                {gpu.vram_used_gb && gpu.vram_total_gb && (
                  <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(gpu.vram_used_gb / gpu.vram_total_gb) * 100}%` }} />
                  </div>
                )}
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Temperature</p>
                <p className="text-xl font-bold mt-1">{gpu.temperature_c || health?.gpu?.temperature_c || 'N/A'}C</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Thermometer className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {Object.keys(services).length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Service Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(services).map(([name, svc]) => (
                <div key={name} data-testid={`svc-health-${name}`} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/20">
                  <div>
                    <p className="font-medium text-sm">{name}</p>
                    <p className="text-xs text-muted-foreground">
                      {svc.latency_ms}ms latency
                      {svc.version && ` | v${svc.version}`}
                    </p>
                  </div>
                  <Badge className={svc.status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-destructive/20 text-destructive'}>
                    {svc.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
